// Utility functions for filtering comparable properties client-side
// FIXED: Properties with missing fields are NOT excluded - only filter when data exists

export const filterComparables = (comparables, filters) => {
  if (!comparables || comparables.length === 0) {
    return [];
  }

  // If no active filters, return all comparables
  if (!hasActiveFilters(filters)) {
    return comparables;
  }

  return comparables.filter(comparable => {
    // Get the raw property data for filtering (try both rawData and direct fields)
    const property = comparable.rawData || comparable;

    // Price Range Filter - ONLY exclude if property HAS price AND it's out of range
    if (filters.priceMin) {
      const price = getPropertyPrice(property, comparable);
      // Only filter if we have a valid price to compare
      if (price !== null && price !== undefined && !isNaN(price)) {
        if (price < parseFloat(filters.priceMin)) return false;
      }
      // If price is missing, DON'T exclude - let property through
    }

    if (filters.priceMax) {
      const price = getPropertyPrice(property, comparable);
      if (price !== null && price !== undefined && !isNaN(price)) {
        if (price > parseFloat(filters.priceMax)) return false;
      }
    }

    // Bedrooms Filter - Use >= for minimum matching (show properties with at least X bedrooms)
    if (filters.bedrooms) {
      const beds = getPropertyBedrooms(property, comparable);
      if (beds !== null && beds !== undefined && !isNaN(beds)) {
        const filterBeds = parseInt(filters.bedrooms);
        // Use >= to include properties with the selected number OR MORE bedrooms
        // e.g., selecting "3 Bedrooms" shows 3, 4, 5+ bedroom properties
        if (beds < filterBeds) return false;
      }
      // If beds is missing, DON'T exclude
    }

    // Bathrooms Filter - minimum threshold
    if (filters.bathrooms) {
      const baths = getPropertyBathrooms(property, comparable);
      if (baths !== null && baths !== undefined && !isNaN(baths)) {
        const filterBaths = parseFloat(filters.bathrooms);
        if (baths < filterBaths) return false;
      }
    }

    // Square Footage Filter
    if (filters.sqftMin) {
      const sqft = getPropertySqft(property, comparable);
      if (sqft !== null && sqft !== undefined && !isNaN(sqft)) {
        if (sqft < parseFloat(filters.sqftMin)) return false;
      }
    }

    if (filters.sqftMax) {
      const sqft = getPropertySqft(property, comparable);
      if (sqft !== null && sqft !== undefined && !isNaN(sqft)) {
        if (sqft > parseFloat(filters.sqftMax)) return false;
      }
    }

    // Year Built Filter
    if (filters.yearBuiltMin) {
      const yearBuilt = getPropertyYearBuilt(property, comparable);
      if (yearBuilt !== null && yearBuilt !== undefined && !isNaN(yearBuilt)) {
        if (yearBuilt < parseInt(filters.yearBuiltMin)) return false;
      }
    }

    if (filters.yearBuiltMax) {
      const yearBuilt = getPropertyYearBuilt(property, comparable);
      if (yearBuilt !== null && yearBuilt !== undefined && !isNaN(yearBuilt)) {
        if (yearBuilt > parseInt(filters.yearBuiltMax)) return false;
      }
    }

    // Property Type Filter - case insensitive partial match
    if (filters.propertyType && filters.propertyType.trim() !== '') {
      const propertyType = getPropertyType(property, comparable);
      if (propertyType) {
        if (!propertyType.toLowerCase().includes(filters.propertyType.toLowerCase())) {
          return false;
        }
      }
      // If propertyType is missing, DON'T exclude
    }

    // Distance Filter
    if (filters.distance) {
      const distance = getPropertyDistance(property, comparable);
      if (distance !== null && distance !== undefined && !isNaN(distance)) {
        const maxDistance = parseFloat(filters.distance);
        if (distance > maxDistance) return false;
      }
    }

    // Feature Filters - only exclude if property explicitly DOESN'T have the feature
    if (filters.hasGarage === true || filters.hasGarage === 'true') {
      // Only exclude if we can confirm NO garage
      const garageStatus = checkGarage(property, comparable);
      if (garageStatus === false) return false; // Confirmed no garage
      // If garageStatus is null (unknown), don't exclude
    }

    if (filters.hasPool === true || filters.hasPool === 'true') {
      const poolStatus = checkPool(property, comparable);
      if (poolStatus === false) return false;
    }

    if (filters.hasFireplace === true || filters.hasFireplace === 'true') {
      const fireplaceStatus = checkFireplace(property, comparable);
      if (fireplaceStatus === false) return false;
    }

    // Days on Market Filter
    if (filters.daysOnMarket && filters.daysOnMarket !== '') {
      const dom = getPropertyDaysOnMarket(property, comparable);
      if (dom !== null && dom !== undefined && !isNaN(dom)) {
        if (!matchesDaysOnMarketRange(dom, filters.daysOnMarket)) return false;
      }
      // If DOM is missing, DON'T exclude
    }

    // Status Filter (Active, Sold, etc.)
    if (filters.status && filters.status !== '' && filters.status !== 'all') {
      const status = getPropertyStatus(property, comparable);
      if (status) {
        const statusLower = status.toLowerCase();
        const filterLower = filters.status.toLowerCase();

        if (filterLower === 'active') {
          if (!statusLower.includes('active') && !statusLower.includes('for sale') && !statusLower.includes('pending')) {
            return false;
          }
        } else if (filterLower === 'sold') {
          if (!statusLower.includes('sold') && !statusLower.includes('closed')) {
            return false;
          }
        }
      }
    }

    return true;
  });
};

// Helper functions to extract property values from BOTH rawData and transformed comparable
const getPropertyPrice = (property, comparable) => {
  // Try rawData first
  let price = property?.ClosePrice ||
              property?.SoldPrice ||
              property?.Price ||
              property?.ListPrice;

  // If not found, try comparable's transformed fields
  if (!price && comparable) {
    // salePrice might be formatted like "$399,000" - extract number
    if (comparable.salePrice) {
      price = parseNumericValue(comparable.salePrice);
    } else if (comparable.listPrice) {
      price = parseNumericValue(comparable.listPrice);
    }
  }

  return price ? parseFloat(price) : null;
};

const getPropertyBedrooms = (property, comparable) => {
  let beds = property?.BedroomsTotal || property?.Bedrooms;

  if (!beds && comparable) {
    beds = comparable.beds;
  }

  return beds ? parseFloat(beds) : null;
};

const getPropertyBathrooms = (property, comparable) => {
  let baths = property?.BathroomsDecimal ||
              property?.BathroomsTotalInteger ||
              property?.BathroomsFull ||
              property?.Bathrooms;

  if (!baths && comparable) {
    baths = comparable.baths;
  }

  return baths ? parseFloat(baths) : null;
};

const getPropertySqft = (property, comparable) => {
  let sqft = property?.LivingArea ||
             property?.GrossLivingArea ||
             property?.SquareFeet ||
             property?.GLA;

  if (!sqft && comparable) {
    sqft = comparable.livingArea || comparable.sqft;
  }

  return sqft ? parseFloat(sqft) : null;
};

const getPropertyYearBuilt = (property, comparable) => {
  let year = property?.YearBuilt;

  if (!year && comparable) {
    year = comparable.yearBuilt;
  }

  return year ? parseInt(year) : null;
};

const getPropertyType = (property, comparable) => {
  let type = property?.PropertySubType || property?.PropertyType;

  if (!type && comparable) {
    type = comparable.style || comparable.propertyType;
  }

  return type || null;
};

const getPropertyDistance = (property, comparable) => {
  let distance = property?.Distance;

  if (distance === null || distance === undefined) {
    distance = comparable?.distance;
  }

  return distance !== null && distance !== undefined ? parseFloat(distance) : null;
};

const getPropertyDaysOnMarket = (property, comparable) => {
  let dom = property?.DaysOnMarket || property?.DOM;

  if (!dom && comparable) {
    dom = comparable.daysOnMarket || comparable.dom;
  }

  return dom ? parseInt(dom) : null;
};

const getPropertyStatus = (property, comparable) => {
  let status = property?.StandardStatus ||
               property?.MlsStatus ||
               property?.Status;

  if (!status && comparable) {
    status = comparable.status;
  }

  return status || null;
};

// Feature check functions - return true/false/null (null = unknown)
const checkGarage = (property, comparable) => {
  // Check for explicit YES
  if (property?.GarageYN === 'Y' || property?.GarageYN === 'Yes') return true;
  if (property?.GarageSpaces && parseFloat(property.GarageSpaces) > 0) return true;
  if (property?.ParkingSpaces && parseFloat(property.ParkingSpaces) > 0) return true;
  if (comparable?.garageSpaces && parseFloat(comparable.garageSpaces) > 0) return true;

  // Check for explicit NO
  if (property?.GarageYN === 'N' || property?.GarageYN === 'No') return false;
  if (property?.GarageSpaces === '0' || property?.GarageSpaces === 0) return false;

  // Unknown
  return null;
};

const checkPool = (property, comparable) => {
  // Check for explicit YES
  if (property?.PoolYN === 'Y' || property?.PoolYN === 'Yes') return true;
  if (property?.PoolFeatures && property.PoolFeatures !== '' && property.PoolFeatures !== 'None' && property.PoolFeatures !== 'NO') return true;
  if (property?.PoolType && property.PoolType !== '' && property.PoolType !== 'None') return true;

  // Check for explicit NO
  if (property?.PoolYN === 'N' || property?.PoolYN === 'No') return false;
  if (property?.PoolFeatures === 'NO' || property?.PoolFeatures === 'None') return false;

  return null;
};

const checkFireplace = (property, comparable) => {
  // Check for explicit YES
  if (property?.FireplaceYN === 'Y' || property?.FireplaceYN === 'Yes') return true;
  if (property?.FireplacesTotal && parseInt(property.FireplacesTotal) > 0) return true;
  if (property?.FireplaceTotal && parseInt(property.FireplaceTotal) > 0) return true;

  // Check for explicit NO
  if (property?.FireplaceYN === 'N' || property?.FireplaceYN === 'No') return false;
  if (property?.FireplacesTotal === '0' || property?.FireplacesTotal === 0) return false;

  return null;
};

const matchesDaysOnMarketRange = (dom, range) => {
  const domValue = parseInt(dom);
  if (isNaN(domValue)) return true; // Don't exclude if invalid

  switch (range) {
    case '0-30':
      return domValue >= 0 && domValue <= 30;
    case '31-60':
      return domValue >= 31 && domValue <= 60;
    case '61-90':
      return domValue >= 61 && domValue <= 90;
    case '91-120':
      return domValue >= 91 && domValue <= 120;
    case '120+':
      return domValue > 120;
    default:
      return true;
  }
};

// Parse numeric value from formatted string like "$399,000"
const parseNumericValue = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;

  // Remove currency symbols, commas, and whitespace
  const cleaned = String(value).replace(/[$,\s]/g, '');
  const num = parseFloat(cleaned);

  return isNaN(num) ? null : num;
};

// Utility to count active filters
export const getActiveFiltersCount = (filters) => {
  if (!filters) return 0;
  return Object.entries(filters).filter(([key, value]) => {
    if (value === '' || value === null || value === undefined) return false;
    if (value === 'all') return false;
    if (value === false) return false;
    return true;
  }).length;
};

// Utility to check if any filters are active
export const hasActiveFilters = (filters) => {
  return getActiveFiltersCount(filters) > 0;
};

// Utility to get human-readable filter summary
export const getFilterSummary = (filters) => {
  const active = [];

  if (filters.priceMin) active.push(`Min $${parseInt(filters.priceMin).toLocaleString()}`);
  if (filters.priceMax) active.push(`Max $${parseInt(filters.priceMax).toLocaleString()}`);
  if (filters.bedrooms) active.push(`${filters.bedrooms}${filters.bedrooms === '5' ? '+' : ''} beds`);
  if (filters.bathrooms) active.push(`${filters.bathrooms}+ baths`);
  if (filters.sqftMin) active.push(`${parseInt(filters.sqftMin).toLocaleString()}+ sqft`);
  if (filters.sqftMax) active.push(`${parseInt(filters.sqftMax).toLocaleString()} max sqft`);
  if (filters.yearBuiltMin) active.push(`Built ${filters.yearBuiltMin}+`);
  if (filters.yearBuiltMax) active.push(`Built before ${filters.yearBuiltMax}`);
  if (filters.propertyType) active.push(filters.propertyType);
  if (filters.distance) active.push(`Within ${filters.distance} mi`);
  if (filters.hasGarage === 'true' || filters.hasGarage === true) active.push('Has garage');
  if (filters.hasPool === 'true' || filters.hasPool === true) active.push('Has pool');
  if (filters.hasFireplace === 'true' || filters.hasFireplace === true) active.push('Has fireplace');
  if (filters.daysOnMarket) active.push(`DOM: ${filters.daysOnMarket}`);
  if (filters.status && filters.status !== 'all') active.push(filters.status);

  return active;
};
